// Videos (DECISIONS "Videos: no time limit, up to 100 MB"): any length, up to
// 100 MB, the largest file Cloudinary's free plan stores. Used by the app and
// the Worker so both check the same numbers.
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export const MAX_VIDEO_MB = 100;

// Cloudinary's free plan converts a video on the fly only when the original
// is 40 MB or less. Bigger videos have their playable copy and cover made
// right after upload ("eager", in the background) with exactly the same
// settings, so one address works for every video.
export const SYNC_VIDEO_BYTES = 40 * 1024 * 1024;
export const VIDEO_PLAYBACK = { transformation: 'q_auto,vc_h264', format: 'mp4' } as const;
export const VIDEO_POSTER = { transformation: 'so_0,c_limit,w_720,q_auto', format: 'jpg' } as const;

// The "eager" upload setting for a big video: the playable MP4 and the cover.
export function eagerVideoTransformations(): string {
  return [VIDEO_PLAYBACK, VIDEO_POSTER].map(item => `${item.transformation}/${item.format}`).join('|');
}

// A trimmed video must fit: a little under the limit, leaving room for the
// file's own headers and the audio.
export const TRIM_TARGET_BYTES = 95 * 1024 * 1024;
const AUDIO_BITS_PER_SECOND = 128_000;
const MIN_VIDEO_BITS_PER_SECOND = 500_000;

export type TrimPlan = { videoBitsPerSecond: number; estimatedBytes: number; lowerQuality: boolean } | null;

// The video bitrate for a trimmed part `seconds` long, cut from a file of
// `sourceBytes` that lasts `sourceSeconds`: the original quality when it
// fits, otherwise lower, but never so low it looks broken. Null when the
// part is too long to fit even then.
export function planTrim(sourceBytes: number, sourceSeconds: number, seconds: number): TrimPlan {
  if (!(sourceSeconds > 0) || !(seconds > 0) || !(sourceBytes > 0)) return null;
  const sourceVideoBits = Math.max(MIN_VIDEO_BITS_PER_SECOND, (sourceBytes * 8) / sourceSeconds - AUDIO_BITS_PER_SECOND);
  const budgetVideoBits = (TRIM_TARGET_BYTES * 8) / seconds - AUDIO_BITS_PER_SECOND;
  if (budgetVideoBits < MIN_VIDEO_BITS_PER_SECOND) return null;
  const videoBitsPerSecond = Math.floor(Math.min(sourceVideoBits, budgetVideoBits));
  return {
    videoBitsPerSecond,
    estimatedBytes: Math.round(((videoBitsPerSecond + AUDIO_BITS_PER_SECOND) * seconds) / 8),
    lowerQuality: videoBitsPerSecond < sourceVideoBits * 0.95,
  };
}

// The longest part (in seconds) that still fits.
export function longestTrim(): number {
  return Math.floor((TRIM_TARGET_BYTES * 8) / (MIN_VIDEO_BITS_PER_SECOND + AUDIO_BITS_PER_SECOND));
}

export function megabytes(bytes: number): string {
  const value = bytes / 1024 / 1024;
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} MB`;
}
